import { useLanguage } from './LanguageContext';
import { siteConfig } from '@/config/site';

/**
 * Hook to get localized paths with language prefix
 */
export function useLocalizedPath() {
  const { language } = useLanguage();

  /**
   * Get a localized path with language prefix (if not default language)
   */
  const getLocalizedPath = (path: string): string => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    
    // Default language (en) doesn't need prefix
    if (language === siteConfig.defaultLanguage) {
      return cleanPath;
    }
    
    return `/${language}${cleanPath}`;
  };

  /**
   * Strip language prefix from a path
   */
  const stripLanguagePrefix = (path: string): string => {
    const langPattern = /^\/(de|es|fr|pt|ru|it|sv|zh)(\/|$)/;
    const match = path.match(langPattern);
    
    if (match) {
      const remainder = path.slice(match[0].length - 1) || '/';
      return remainder.startsWith('/') ? remainder : `/${remainder}`;
    }
    
    return path;
  };

  /**
   * Get language from path
   */
  const getLanguageFromPath = (path: string): string | null => {
    const langPattern = /^\/(de|es|fr|pt|ru|it|sv|zh)(\/|$)/;
    const match = path.match(langPattern);
    return match ? match[1] : null;
  };

  return {
    language,
    getLocalizedPath,
    stripLanguagePrefix,
    getLanguageFromPath,
  };
}

/**
 * Supported language codes (excluding default 'en')
 */
export const LANGUAGE_PREFIXES = ['de', 'es', 'fr', 'pt', 'ru', 'it', 'sv', 'zh'] as const;

/**
 * All supported languages including default
 */
export const ALL_LANGUAGES = ['en', ...LANGUAGE_PREFIXES] as const;
